// src/pages/admin/Badges.jsx
import React, { useState, useEffect } from 'react';
import { useOptions } from '../../context/OptionsContext';
import { adminApi } from '../../api/adminApi';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Stack,
  Alert,
  CircularProgress,
  Menu,
  MenuItem,
} from '@mui/material';
import { Add, Edit, Delete, Check, Close, Refresh, LocalOffer, MoreVert } from '@mui/icons-material';

const Badges = () => {
  const { badges, loading, fetchAllOptions } = useOptions();
  const [openDialog, setOpenDialog] = useState(false);
  const [editingBadge, setEditingBadge] = useState(null);
  const [formData, setFormData] = useState({ name: '', displayOrder: 0 });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Menu State
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedBadge, setSelectedBadge] = useState(null);

  const handleMenuOpen = (event, badge) => {
    setAnchorEl(event.currentTarget);
    setSelectedBadge(badge);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleOpenDialog = (badge = null) => {
    if (badge) {
      setEditingBadge(badge);
      setFormData({ name: badge.name, displayOrder: badge.displayOrder || 0 });
    } else {
      setEditingBadge(null);
      setFormData({ name: '', displayOrder: 0 });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingBadge(null);
    setError(null);
  };

  const handleSave = async () => {
    try {
      if (editingBadge) {
        await adminApi.updateBadge(editingBadge.id, formData);
        setSuccess('Badge updated successfully!');
      } else {
        await adminApi.createBadge(formData);
        setSuccess('Badge created successfully!');
      }
      handleCloseDialog();
      fetchAllOptions();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save badge');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this badge?')) {
      try {
        await adminApi.deleteBadge(id);
        setSuccess('Badge deleted successfully!');
        fetchAllOptions();
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to delete badge');
      }
    }
  };

  // ✅ Toggle Status
  const handleToggleStatus = async (badge) => {
    try {
      const updatedStatus = !badge.isActive;
      // Send just the isActive field to update status
      await adminApi.updateBadge(badge.id, { isActive: updatedStatus });
      setSuccess(`Badge "${badge.name}" ${updatedStatus ? 'activated' : 'deactivated'} successfully`);
      fetchAllOptions();
      handleMenuClose();
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to toggle badge status');
    }
  };

  if (loading) {
    return <CircularProgress />;
  }

  return (
    <Box>
      {/* Header */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h5" fontWeight="bold">
              Badges
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage product badge options
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={<Refresh />} onClick={fetchAllOptions}>
              Refresh
            </Button>
            <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenDialog()}>
              Add Badge
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {/* Badges Table */}
      <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: 'grey.50' }}>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Display Order</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {badges.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="text.secondary">
                      No badges found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                badges.map((badge) => (
                  <TableRow key={badge.id} hover>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <LocalOffer fontSize="small" color="primary" />
                        <Typography variant="body2" fontWeight="600">
                          {badge.name}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>{badge.displayOrder || 0}</TableCell>
                    <TableCell>
                      <Chip
                        icon={badge.isActive ? <Check /> : <Close />}
                        label={badge.isActive ? 'Active' : 'Inactive'}
                        size="small"
                        color={badge.isActive ? 'success' : 'error'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={(e) => handleMenuOpen(e, badge)}>
                        <MoreVert />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => { handleOpenDialog(selectedBadge); handleMenuClose(); }}>
          <Edit fontSize="small" sx={{ mr: 1 }} /> Edit
        </MenuItem>
        <MenuItem onClick={() => handleToggleStatus(selectedBadge)}>
          {selectedBadge?.isActive ? (
            <>
              <Close fontSize="small" sx={{ mr: 1 }} /> Deactivate
            </>
          ) : (
            <>
              <Check fontSize="small" sx={{ mr: 1 }} /> Activate
            </>
          )}
        </MenuItem>
        <MenuItem onClick={() => handleDelete(selectedBadge?.id)} sx={{ color: 'error.main' }}>
          <Delete fontSize="small" sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>

      {/* Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingBadge ? 'Edit Badge' : 'Add New Badge'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Badge Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
            <TextField
              fullWidth
              label="Display Order"
              type="number"
              value={formData.displayOrder}
              onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 0 })}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>
            {editingBadge ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Badges;